import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { jsPDF } from 'jspdf';
import QRCodeStyling from 'qr-code-styling';
import api from '../../utils/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const REFERRAL_PDF_BACKGROUND = `${process.env.PUBLIC_URL}/img/referral/referral-background.png`;
const REFERRAL_PDF_POWERED_BY_LOGO = `${process.env.PUBLIC_URL}/img/referral/powered-by-idotoo.png`;
const PLUS_JAKARTA_BOLD = `${process.env.PUBLIC_URL}/fonts/PlusJakartaSans-Bold.ttf`;
const PLUS_JAKARTA_MEDIUM = `${process.env.PUBLIC_URL}/fonts/PlusJakartaSans-Medium.ttf`;

const fontBase64Cache = {};

function SupplierReferModal({ open, onClose, businessId, businessName }) {
  const [loading, setLoading] = useState(false);
  const [copying, setCopying] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState('');

  useEffect(() => {
    if (!open) {
      return;
    }

    let isMounted = true;

    const fetchInviteLink = async () => {
      setLoading(true);
      setError('');
      setCopySuccess('');
      try {
        const response = await api.get('/api/supplier/invite-link', {
          params: businessId ? { businessId } : undefined
        });
        if (isMounted) {
          setInviteData(response.data);
        }
      } catch (fetchError) {
        if (isMounted) {
          const message = fetchError?.response?.data?.message || 'Unable to load referral link right now.';
          setError(message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchInviteLink();

    return () => {
      isMounted = false;
    };
  }, [open, businessId]);

  useEffect(() => {
    let cancelled = false;

    const generateQrCode = async () => {
      if (!inviteData?.link) {
        setQrCodeDataUrl('');
        return;
      }

      try {
        const qrCode = new QRCodeStyling({
          width: 320,
          height: 320,
          type: 'canvas',
          data: inviteData.link,
          margin: 8,
          qrOptions: {
            errorCorrectionLevel: 'Q'
          },
          dotsOptions: {
            type: 'dots',
            color: '#EB3985'
          },
          cornersSquareOptions: {
            type: 'extra-rounded',
            color: '#EB3985'
          },
          cornersDotOptions: {
            type: 'dot',
            color: '#EB3985'
          },
          backgroundOptions: {
            color: '#FFFFFF'
          }
        });

        const qrBlob = await qrCode.getRawData('png');
        if (!(qrBlob instanceof Blob)) {
          throw new Error('Unable to generate QR image');
        }

        const dataUrl = await blobToDataUrl(qrBlob);
        if (!cancelled) {
          setQrCodeDataUrl(dataUrl);
        }
      } catch (qrError) {
        if (!cancelled) {
          setQrCodeDataUrl('');
          setError('Could not generate QR code right now.');
        }
      }
    };

    generateQrCode();

    return () => {
      cancelled = true;
    };
  }, [inviteData?.link]);

  const resolvedBusinessName = inviteData?.businessName || businessName || 'Your business';

  const rasterizeImageToPngDataUrl = (src, maxWidth, maxHeight) =>
    new Promise((resolve) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        const ratio = Math.min(maxWidth / image.width, maxHeight / image.height);
        const drawWidth = Math.max(1, Math.round(image.width * ratio));
        const drawHeight = Math.max(1, Math.round(image.height * ratio));
        const canvas = document.createElement('canvas');
        canvas.width = drawWidth;
        canvas.height = drawHeight;
        const context = canvas.getContext('2d');
        if (!context) {
          resolve(null);
          return;
        }
        context.drawImage(image, 0, 0, drawWidth, drawHeight);
        resolve(canvas.toDataURL('image/png'));
      };
      image.onerror = () => resolve(null);
      image.src = src;
    });

  const blobToDataUrl = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const fetchFontAsBase64 = async (fontUrl) => {
    if (fontBase64Cache[fontUrl]) {
      return fontBase64Cache[fontUrl];
    }

    try {
      const response = await fetch(fontUrl);
      if (!response.ok) {
        return null;
      }

      const arrayBuffer = await response.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      const chunkSize = 0x8000;
      let binary = '';

      for (let i = 0; i < bytes.length; i += chunkSize) {
        const chunk = bytes.subarray(i, i + chunkSize);
        binary += String.fromCharCode.apply(null, chunk);
      }

      const base64 = btoa(binary);
      fontBase64Cache[fontUrl] = base64;
      return base64;
    } catch (fontError) {
      return null;
    }
  };

  const registerPlusJakartaFonts = async (pdf) => {
    const result = { bold: false, medium: false };

    const boldBase64 = await fetchFontAsBase64(PLUS_JAKARTA_BOLD);
    if (boldBase64) {
      try {
        pdf.addFileToVFS('PlusJakartaSans-Bold.ttf', boldBase64);
        pdf.addFont('PlusJakartaSans-Bold.ttf', 'PlusJakartaSans', 'bold');
        result.bold = true;
      } catch (error) {
        result.bold = false;
      }
    }

    const mediumBase64 = await fetchFontAsBase64(PLUS_JAKARTA_MEDIUM);
    if (mediumBase64) {
      try {
        pdf.addFileToVFS('PlusJakartaSans-Medium.ttf', mediumBase64);
        pdf.addFont('PlusJakartaSans-Medium.ttf', 'PlusJakartaSans', 'normal');
        result.medium = true;
      } catch (error) {
        result.medium = false;
      }
    }

    return result;
  };

  const normalizeImageSource = (source) => {
    if (!source) {
      return '';
    }
    if (source.startsWith('data:') || source.startsWith('http://') || source.startsWith('https://')) {
      return source;
    }
    if (source.startsWith('/')) {
      return `${API_BASE_URL}${source}`;
    }
    return `${API_BASE_URL}/${source}`;
  };

  const handleCopyLink = async () => {
    if (!inviteData?.link || copying) {
      return;
    }
    setCopying(true);
    setCopySuccess('');
    try {
      await navigator.clipboard.writeText(inviteData.link);
      setCopySuccess('Referral link copied.');
    } catch (copyError) {
      setError('Could not copy link. Please copy it manually.');
    } finally {
      setCopying(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!inviteData?.code || !qrCodeDataUrl || downloading) {
      return;
    }
    setDownloading(true);
    setError('');
    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'a4'
      });
      const fontStatus = await registerPlusJakartaFonts(pdf);

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const verticalOffset = 40;
      const backgroundDataUrl = await rasterizeImageToPngDataUrl(
        REFERRAL_PDF_BACKGROUND,
        pageWidth,
        pageHeight
      );
      const supplierLogoDataUrl = await rasterizeImageToPngDataUrl(
        inviteData?.businessLogoDataUrl || normalizeImageSource(inviteData?.businessLogo),
        260,
        110
      );
      const logoDataUrl = await rasterizeImageToPngDataUrl(`${process.env.PUBLIC_URL}/img/logo.svg`, 300, 90);
      const poweredByLogoDataUrl = await rasterizeImageToPngDataUrl(
        REFERRAL_PDF_POWERED_BY_LOGO,
        220,
        80
      );

      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');

      if (backgroundDataUrl) {
        pdf.addImage(backgroundDataUrl, 'PNG', 0, 0, pageWidth, pageHeight);
      }

      const supplierLogoMaxWidth = 170;
      const supplierLogoMaxHeight = 68;
      const supplierLogoBoxY = 40 + verticalOffset;
      const supplierLogoBoxBottom = supplierLogoBoxY + supplierLogoMaxHeight;

      if (supplierLogoDataUrl) {
        let supplierLogoWidth = supplierLogoMaxWidth;
        let supplierLogoHeight = supplierLogoMaxHeight;

        try {
          const logoProps = pdf.getImageProperties(supplierLogoDataUrl);
          if (logoProps?.width && logoProps?.height) {
            const scale = Math.min(
              supplierLogoMaxWidth / logoProps.width,
              supplierLogoMaxHeight / logoProps.height
            );
            supplierLogoWidth = Math.max(1, logoProps.width * scale);
            supplierLogoHeight = Math.max(1, logoProps.height * scale);
          }
        } catch (error) {
          // Fallback keeps previous bounded size if metadata cannot be read.
        }

        const supplierLogoX = (pageWidth - supplierLogoWidth) / 2;
        const supplierLogoY = supplierLogoBoxY + ((supplierLogoMaxHeight - supplierLogoHeight) / 2);
        pdf.addImage(supplierLogoDataUrl, 'PNG', supplierLogoX, supplierLogoY, supplierLogoWidth, supplierLogoHeight);
      }

      const maxNameWidth = pageWidth - 120;
      const maxNameLines = 3;
      let supplierNameFontSize = 42;
      let supplierNameLines = [];

      while (supplierNameFontSize >= 28) {
        pdf.setFont(fontStatus.bold ? 'PlusJakartaSans' : 'helvetica', 'bold');
        pdf.setFontSize(supplierNameFontSize);
        supplierNameLines = pdf.splitTextToSize(resolvedBusinessName, maxNameWidth);
        if (supplierNameLines.length <= maxNameLines) {
          break;
        }
        supplierNameFontSize -= 2;
      }

      if (supplierNameLines.length > maxNameLines) {
        supplierNameLines = supplierNameLines.slice(0, maxNameLines);
        const lastLine = String(supplierNameLines[maxNameLines - 1] || '');
        supplierNameLines[maxNameLines - 1] = `${lastLine.replace(/\s+$/, '')}…`;
      }

      const nameLineHeight = supplierNameFontSize * 1.2;
      const nameBlockTopY = supplierLogoBoxBottom + 28; // Slightly larger gap below logo
      const nameFirstBaselineY = nameBlockTopY + supplierNameFontSize;
      const nameBlockHeight = supplierNameFontSize + ((supplierNameLines.length - 1) * nameLineHeight);

      pdf.setFont(fontStatus.bold ? 'PlusJakartaSans' : 'helvetica', 'bold');
      pdf.setFontSize(supplierNameFontSize);
      pdf.text(supplierNameLines, pageWidth / 2, nameFirstBaselineY, {
        align: 'center',
        lineHeightFactor: 1.2
      });

      const inviteTextY = nameBlockTopY + nameBlockHeight + 56;
      const qrSize = 230;
      const textToQrGap = 34;
      const qrY = inviteTextY + textToQrGap;
      const scanTextY = qrY + qrSize + textToQrGap;
      const qrX = (pageWidth - qrSize) / 2;
      pdf.addImage(qrCodeDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);

      pdf.setFont(fontStatus.medium ? 'PlusJakartaSans' : 'helvetica', 'normal');
      pdf.setFontSize(24);
      pdf.text('Has invited you to join idotoo', pageWidth / 2, inviteTextY, { align: 'center' });

      pdf.setFont(fontStatus.medium ? 'PlusJakartaSans' : 'helvetica', 'normal');
      pdf.setFontSize(24);
      pdf.text('Scan to start planning your dream wedding', pageWidth / 2, scanTextY, {
        align: 'center',
        maxWidth: pageWidth - 120
      });

      pdf.setFont(fontStatus.medium ? 'PlusJakartaSans' : 'helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.text('Powered by', pageWidth / 2, pageHeight - 68, { align: 'center' });

      if (poweredByLogoDataUrl) {
        const poweredLogoWidth = 102;
        const poweredLogoHeight = 28;
        const poweredLogoX = (pageWidth - poweredLogoWidth) / 2;
        pdf.addImage(poweredByLogoDataUrl, 'PNG', poweredLogoX, pageHeight - 58, poweredLogoWidth, poweredLogoHeight);
      } else if (logoDataUrl) {
        const fallbackLogoWidth = 65;
        const fallbackLogoHeight = 20;
        const fallbackLogoX = (pageWidth - fallbackLogoWidth) / 2;
        pdf.addImage(logoDataUrl, 'PNG', fallbackLogoX, pageHeight - 54, fallbackLogoWidth, fallbackLogoHeight);
      }

      pdf.setFont(fontStatus.medium ? 'PlusJakartaSans' : 'helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.text('www.idotoo.ai', pageWidth / 2, pageHeight - 10, { align: 'center' });

      pdf.save(`${resolvedBusinessName.replace(/\s+/g, '-').toLowerCase()}-referral-qr.pdf`);
    } catch (pdfError) {
      setError('Could not generate PDF right now. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Refer</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Share your referral link with couples. When they register, you will be automatically shortlisted and a chat will be started.
          </Typography>

          {error && <Alert severity="error">{error}</Alert>}
          {copySuccess && <Alert severity="success">{copySuccess}</Alert>}

          <TextField
            label="Your referral link"
            fullWidth
            value={inviteData?.link || ''}
            InputProps={{ readOnly: true }}
            disabled={loading}
          />

          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
            {qrCodeDataUrl ? (
              <Box
                component="img"
                src={qrCodeDataUrl}
                alt="Referral QR code"
                sx={{ width: 220, height: 220, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
              />
            ) : (
              <Box
                sx={{
                  width: 220,
                  height: 220,
                  borderRadius: 1,
                  border: '1px dashed',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary'
                }}
              >
                QR code preview
              </Box>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
        <Button variant="outlined" onClick={handleCopyLink} disabled={loading || !inviteData?.link || copying}>
          Copy link
        </Button>
        <Button variant="contained" onClick={handleDownloadPdf} disabled={loading || !inviteData?.link || downloading}>
          Download PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SupplierReferModal;
